import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from './Sidebar';
import Header from './Header';
import PillarBar from './PillarBar';
import ScreenshotCapture from '../ScreenshotCapture';
import { Menu, X, Link2, LogIn, Eye } from 'lucide-react';
import { derivePageId } from '../../utils/pageId';

function ImpersonationBanner() {
    const [name, setName] = useState(null);
    useEffect(() => {
        setName(localStorage.getItem('crm_impersonating'));
    }, []);

    if (!name) return null;

    const isViewAs = name?.startsWith('View As:');

    const endImpersonation = () => {
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_impersonating');
        window.close();
    };

    return (
        <div className={`${isViewAs ? 'bg-indigo-600' : 'bg-blue-600'} text-white text-center py-2 px-4 text-xs font-medium flex items-center justify-center gap-3`} data-testid="impersonation-banner">
            <div className="flex items-center gap-2">
                <Eye className="w-3.5 h-3.5" />
                <span>{isViewAs ? (
                    <><strong>{name.replace('View As: ', '')}</strong> — You are previewing this user's view. Navigation and permissions reflect their role assignments.</>
                ) : (
                    <>Viewing as <strong>{name}</strong> (impersonation mode)</>
                )}</span>
            </div>
            <button onClick={endImpersonation} className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-semibold transition-colors">
                {isViewAs ? 'Exit Preview' : 'End Session'}
            </button>
        </div>
    );
}

export default function AppLayout({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);

    // Redirect to onboarding if not completed (but don't redirect if already on onboarding or settings)
    useEffect(() => {
        if (user && user.onboarding_completed === false &&
            !location.pathname.startsWith('/onboarding') &&
            !location.pathname.startsWith('/settings')) {
            window.location.href = '/onboarding';
        }
    }, [user, location.pathname]);

    // SSO event alert (runs once after SSO redirect)
    const [ssoAlert, setSsoAlert] = useState(null);
    useEffect(() => {
        const ssoEvent = sessionStorage.getItem('sso_event');
        if (ssoEvent) {
            sessionStorage.removeItem('sso_event');
            if (ssoEvent === 'linked') {
                setSsoAlert({
                    icon: Link2,
                    title: 'Multiplai account linked',
                    message: 'Your Multiplai account has been linked to your existing account. You can now use either method to sign in.',
                });
            }
        }
    }, []);

    // Suggest Multiplai SSO linking for eligible domains
    const SSO_DOMAINS = ['@go.team', '@multiplai.tech', '@aicoaches.com'];
    const [ssoSuggestionDismissed, setSsoSuggestionDismissed] = useState(() =>
        sessionStorage.getItem('sso_link_dismissed') === '1'
    );
    const showSsoSuggestion = user
        && user.email
        && !user.sso_provider
        && SSO_DOMAINS.some(d => user.email.toLowerCase().endsWith(d))
        && !ssoSuggestionDismissed;

    const dismissSsoSuggestion = () => {
        sessionStorage.setItem('sso_link_dismissed', '1');
        setSsoSuggestionDismissed(true);
    };

    // Close sidebar on route change (mobile)
    useEffect(() => {
        const handlePopState = () => setMobileOpen(false);
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    return (
        <div className="flex flex-col h-screen overflow-hidden" style={{ backgroundColor: '#FAFAF5' }}>
            <ImpersonationBanner />
            <div className="flex flex-1 overflow-hidden relative">
                {/* Mobile overlay */}
                {mobileOpen && (
                    <div
                        className="fixed inset-0 bg-black/40 z-40 lg:hidden"
                        onClick={() => setMobileOpen(false)}
                        data-testid="sidebar-overlay"
                    />
                )}

                {/* Sidebar: hidden on mobile, slide-in when mobileOpen */}
                <div className={`
                    fixed inset-y-0 left-0 z-50 lg:static lg:z-auto
                    transform transition-transform duration-200 ease-out
                    ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                `}>
                    <Sidebar onNavigate={() => setMobileOpen(false)} />
                </div>

                <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                    <Header onMenuToggle={() => setMobileOpen(prev => !prev)} />
                    <PillarBar />
                    <main className="flex-1 overflow-y-auto">
                        {/* Prompt Forge host: every authenticated route gets
                            a stable data-page-id derived from the URL via
                            derivePageId(). No per-route tagging needed. */}
                        <div
                            className="p-3 sm:p-4 md:p-6"
                            data-host-page
                            data-page-id={derivePageId(location.pathname)}
                        >
                            {ssoAlert && (
                                <div className="mb-4 bg-white border border-stone-200 rounded-xl p-4 flex items-start gap-3 shadow-sm animate-fade-in">
                                    <div className="flex-shrink-0 w-9 h-9 bg-stone-900 rounded-lg flex items-center justify-center">
                                        <ssoAlert.icon className="w-4.5 h-4.5 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                                            {ssoAlert.title}
                                        </p>
                                        <p className="text-sm text-stone-500 mt-0.5">{ssoAlert.message}</p>
                                    </div>
                                    <button
                                        onClick={() => setSsoAlert(null)}
                                        className="flex-shrink-0 p-1 rounded-md text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                            {showSsoSuggestion && (
                                <div className="mb-4 bg-stone-50 border border-stone-200 rounded-xl p-4 flex items-start gap-3 animate-fade-in">
                                    <div className="flex-shrink-0 w-9 h-9 bg-stone-800 rounded-lg flex items-center justify-center">
                                        <LogIn className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                                            Link your Multiplai account
                                        </p>
                                        <p className="text-sm text-stone-500 mt-0.5">
                                            Sign in with Multiplai to link your accounts for faster access next time.
                                        </p>
                                        <button
                                            onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/authorize`; }}
                                            className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-xs font-medium rounded-lg hover:bg-stone-800 transition-colors"
                                        >
                                            <LogIn className="w-3.5 h-3.5" />
                                            Link with Multiplai
                                        </button>
                                    </div>
                                    <button
                                        onClick={dismissSsoSuggestion}
                                        className="flex-shrink-0 p-1 rounded-md text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                            {children}
                        </div>
                    </main>
                    <ScreenshotCapture />
                </div>
            </div>
        </div>
    );
}
